import dayjs from 'dayjs';
import * as yup from 'yup';
// Types
import {
  CampaignMediaType,
  CampaignTaskType,
  CbDueDateType,
  CbDisplayPeriod,
  CbFixedDateType,
  CreateCampaignContentTaskInput,
  CreateCampaignContentItemInput,
  CreateCampaignTaskV2Input,
} from 'api/graphql-global-types';
import { GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks } from 'api/campaignV2/types/GetCampaignsV2';
import { DueDateFields } from 'api/campaignV2/types/DueDateFields';
// Helpers
import { ValueLabelOption } from './campaignEditModal';
import { TimeLineDeliverable } from './deliverables';

const DATE_RANGE_SPECIFIC_TYPES = [CbDueDateType.FIXED, CbDueDateType.RANGE];

const VALID_CAMPAIGN_CONTENT_TASK_FIELDS = [
  'contentCbDisplayPeriod',
  'contentDueDateType',
  'contentFixedDate',
  'contentFixedDateType',
  'contentRangeFrom',
  'contentRangeTo',
  'contentValueInterval',
  'type',
  'title',
  'mediaType',
  'description',
] as const;

export type ContentOnlyModalFormData = {
  mediaType: CampaignMediaType;
  title: string;
  description: string;
  dueDateType: CbDueDateType | 'specific';
  dueDate?: Date;
  approvalNumber?: number;
  approvalUnit?: CbDisplayPeriod;
  dateRangeType?: CbDueDateType.FIXED | CbDueDateType.RANGE;
  singleDateType?: CbFixedDateType | null;
  startDate?: Date;
  endDate?: Date;
};

export type UgcContentPieceFormData = {
  mediaType: CampaignMediaType;
  title: string;
  description: string;
};

// Multi-piece UGC deliverable used by the custom-campaign flow. Mirrors
// millions-fe: a named deliverable with one or more content pieces plus a
// shared content due-date config.
export type UgcDeliverableFormData = {
  name: string;
  description: string;
  contentPieces: UgcContentPieceFormData[];
  dueDateType: CbDueDateType | 'specific';
  dueDate?: Date;
  approvalNumber?: number;
  approvalUnit?: CbDisplayPeriod;
  dateRangeType?: CbDueDateType.FIXED | CbDueDateType.RANGE;
  singleDateType?: CbFixedDateType | null;
  startDate?: Date;
  endDate?: Date;
};

export const DUE_DATE_OPTIONS: ValueLabelOption[] = [
  {
    label: 'Application Approval-Based Timeline',
    value: CbDueDateType.APPLICATION_BASED,
  },
  { label: 'Specific Date or Date Range', value: 'specific' },
];

export const SINGLE_DATE_TYPE_OPTIONS: ValueLabelOption[] = [
  { label: 'Before', value: CbFixedDateType.BEFORE },
  { label: 'On', value: CbFixedDateType.EXACT },
];

// Due-date-type guards for the conditional schema below. Each returns true
// only when its field is relevant for the chosen due date type, so unrelated
// optional fields never raise spurious errors.
const isApplicationBased = (dueDateType: unknown): boolean =>
  dueDateType === CbDueDateType.APPLICATION_BASED;

const isSpecificFixed = (
  dueDateType: unknown,
  dateRangeType: unknown
): boolean =>
  dueDateType === 'specific' && dateRangeType === CbDueDateType.FIXED;

const isSpecificRange = (
  dueDateType: unknown,
  dateRangeType: unknown
): boolean =>
  dueDateType === 'specific' && dateRangeType === CbDueDateType.RANGE;

// NOTE: Used for UGC deliverable validation in edit custom campaign.
// The return type is inferred: annotating it as AnyObjectSchema triggers a yup
// variance mismatch, so we silence the boundary-type lint rule instead.
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const ugcDeliverableChecklistValidation = () =>
  yup.object().shape({
    name: yup
      .string()
      .trim()
      .required('Deliverable name is required')
      .max(120, 'Name must be at most 120 characters'),
    description: yup
      .string()
      .trim()
      .required('Description is required')
      .max(5000, 'Description must be at most 5000 characters'),
    contentPieces: yup
      .array()
      .of(
        yup.object().shape({
          mediaType: yup.string().required('Media type is required'),
          title: yup
            .string()
            .trim()
            .required('Content title is required')
            .max(120, 'Title must be at most 120 characters'),
          description: yup
            .string()
            .trim()
            .required('Instructions are required')
            .max(5000, 'Instructions must be at most 5000 characters'),
        })
      )
      .min(1, 'At least one content piece is required'),
    dueDateType: yup.string().required('Content due date is required'),
    approvalNumber: yup
      .number()
      // Empty Select -> '' / NaN: coerce to undefined so we surface the
      // friendly "Number is required" message instead of a cast type error.
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null || Number.isNaN(value)
          ? undefined
          : value
      )
      .when('dueDateType', {
        is: isApplicationBased,
        then: (schema) =>
          schema.required('Number is required').min(1, 'Number is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    approvalUnit: yup.string().when('dueDateType', {
      is: isApplicationBased,
      then: (schema) => schema.required('Unit is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    dateRangeType: yup.string().when('dueDateType', {
      is: (dueDateType: unknown) => dueDateType === 'specific',
      then: (schema) => schema.required('Date type is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    singleDateType: yup
      .string()
      .nullable()
      .when(['dueDateType', 'dateRangeType'], {
        is: isSpecificFixed,
        then: (schema) => schema.required('Timing is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    dueDate: yup.mixed().when(['dueDateType', 'dateRangeType'], {
      is: isSpecificFixed,
      then: (schema) => schema.required('Date is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    startDate: yup.mixed().when(['dueDateType', 'dateRangeType'], {
      is: isSpecificRange,
      then: (schema) => schema.required('Start date is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    endDate: yup.mixed().when(['dueDateType', 'dateRangeType'], {
      is: isSpecificRange,
      then: (schema) => schema.required('End date is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

export const generateContentOnlyDefaultFormData =
  (): Partial<ContentOnlyModalFormData> => {
    return {
      mediaType: CampaignMediaType.VIDEO,
      title: '',
      description: '',
      dueDateType: CbDueDateType.APPLICATION_BASED,
      approvalNumber: 1,
      approvalUnit: CbDisplayPeriod.WEEKS,
    };
  };

export const generateUgcDeliverableDefaultFormData =
  (): UgcDeliverableFormData => {
    return {
      name: '',
      description: '',
      contentPieces: [
        {
          mediaType: CampaignMediaType.VIDEO,
          title: '',
          description: '',
        },
      ],
      dueDateType: CbDueDateType.APPLICATION_BASED,
      approvalNumber: 1,
      approvalUnit: CbDisplayPeriod.WEEKS,
    };
  };

// Maps the deliverable's due date config (nested `dueDate`, flat local fields,
// or a merged DueDateFields) into the due-date portion of the modal form data.
const mapUgcDueDateToFormData = (
  item: any
): Pick<
  UgcDeliverableFormData,
  | 'dueDateType'
  | 'dueDate'
  | 'singleDateType'
  | 'startDate'
  | 'endDate'
  | 'approvalNumber'
  | 'approvalUnit'
  | 'dateRangeType'
> => {
  const hasNestedDueDate = item.dueDate && typeof item.dueDate === 'object';

  const dueDateType = hasNestedDueDate
    ? item.dueDate.type
    : item.contentDueDateType;
  const fixedDate = hasNestedDueDate
    ? item.dueDate.fixedDate
    : item.contentFixedDate;
  const fixedType = hasNestedDueDate
    ? item.dueDate.fixedType
    : item.contentFixedDateType;
  const rangeFrom = hasNestedDueDate
    ? item.dueDate.rangeFrom
    : item.contentRangeFrom;
  const rangeTo = hasNestedDueDate ? item.dueDate.rangeTo : item.contentRangeTo;
  const intervalDays = hasNestedDueDate
    ? item.dueDate.intervalDays
    : item.contentValueInterval;

  const { intervalDays: displayInterval, displayPeriod } = mapDueDateToFormData(
    hasNestedDueDate ? item.dueDate : null
  );

  return {
    dueDateType: DATE_RANGE_SPECIFIC_TYPES.includes(dueDateType)
      ? 'specific'
      : dueDateType ?? CbDueDateType.APPLICATION_BASED,
    dueDate: fixedDate ? new Date(fixedDate) : undefined,
    singleDateType: fixedType,
    startDate: rangeFrom ? new Date(rangeFrom) : undefined,
    endDate: rangeTo ? new Date(rangeTo) : undefined,
    approvalNumber: hasNestedDueDate ? displayInterval : intervalDays,
    approvalUnit: hasNestedDueDate
      ? displayPeriod
      : item.contentCbDisplayPeriod,
    dateRangeType: DATE_RANGE_SPECIFIC_TYPES.includes(dueDateType)
      ? dueDateType
      : undefined,
  };
};

// Builds the list of content pieces from a UGC task. Prefers the nested
// `contentItems` (loaded CampaignUgcTask / edited local state), falling back to
// a single piece derived from the flat task fields for older single-piece data.
const mapUgcContentPieces = (item: any): UgcContentPieceFormData[] => {
  const pieces = item.contentItems
    ?.filter((piece: any) => piece.type !== CampaignTaskType.FINAL_CONTENT)
    .map((piece: any) => ({
      mediaType: piece.mediaType ?? CampaignMediaType.VIDEO,
      title: piece.title ?? '',
      description: piece.description ?? '',
    }));

  if (pieces?.length) return pieces;

  return [
    {
      mediaType: item.mediaType ?? CampaignMediaType.VIDEO,
      title: item.title ?? '',
      description: item.description ?? '',
    },
  ];
};

// Single-piece mapper retained for the timeline social flow's ContentOnlyModal,
// which still edits one content item at a time.
export const mapUgcEditingItemToFormData = (
  editingItem: GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks
): Partial<ContentOnlyModalFormData> => {
  if (!editingItem) return {};

  const item = editingItem as any;

  return {
    mediaType: item.mediaType ?? CampaignMediaType.VIDEO,
    title: item.title ?? '',
    description: item.description ?? '',
    ...mapUgcDueDateToFormData(item),
  };
};

export const mapUgcEditingDeliverableToFormData = (
  editingItem: GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks
): UgcDeliverableFormData => {
  const defaults = generateUgcDeliverableDefaultFormData();
  if (!editingItem) return defaults;

  const item = editingItem as any;

  const contentItem = item.contentItems?.find(
    (task: any) => task.type === CampaignTaskType.CONTENT
  );

  // The parent task and its content item can each hold a partial dueDate: the
  // parent may carry the due date type while the per-piece interval/fixed date
  // lives on the content item (or vice versa). Merge them field-by-field,
  // preferring whichever side actually has a value, so the form hydrates the
  // approval number/unit and dates instead of falling back to defaults.
  const mergedDueDate =
    item.dueDate || contentItem?.dueDate
      ? mergeDueDate(item.dueDate, contentItem?.dueDate)
      : undefined;

  const dueDate = mapUgcDueDateToFormData({
    ...item,
    dueDate: mergedDueDate,
    contentDueDateType:
      item.contentDueDateType ?? contentItem?.contentDueDateType,
    contentFixedDate: item.contentFixedDate ?? contentItem?.contentFixedDate,
    contentFixedDateType:
      item.contentFixedDateType ?? contentItem?.contentFixedDateType,
    contentRangeFrom: item.contentRangeFrom ?? contentItem?.contentRangeFrom,
    contentRangeTo: item.contentRangeTo ?? contentItem?.contentRangeTo,
    contentValueInterval:
      item.contentValueInterval ?? contentItem?.contentValueInterval,
    contentCbDisplayPeriod:
      item.contentCbDisplayPeriod ?? contentItem?.contentCbDisplayPeriod,
  });

  return {
    name: item.name ?? item.title ?? '',
    description: item.description ?? '',
    contentPieces: mapUgcContentPieces(item),
    ...dueDate,
  };
};

/**
 * Combines the parent task dueDate with its content item dueDate, taking each
 * field from whichever side has a non-null value (parent first). Used when the
 * API splits the due date config across the UGC task and its content piece.
 */
const mergeDueDate = (
  parentDueDate?: DueDateFields | null,
  contentDueDate?: DueDateFields | null
): DueDateFields => {
  const pick = <K extends keyof DueDateFields>(key: K): DueDateFields[K] =>
    parentDueDate?.[key] ?? contentDueDate?.[key];

  return {
    __typename: 'DueDateConfiguration',
    type: pick('type'),
    fixedType: pick('fixedType'),
    fixedDate: pick('fixedDate'),
    rangeFrom: pick('rangeFrom'),
    rangeTo: pick('rangeTo'),
    intervalDays: pick('intervalDays'),
  };
};

export const mapContentOnlyFormDataToCreateCampaignContentTaskInput = (
  data: ContentOnlyModalFormData
): CreateCampaignContentTaskInput => {
  return {
    type: CampaignTaskType.CONTENT,
    mediaType: data.mediaType,
    title: data.title,
    description: data.description,
    contentDueDateType:
      data.dueDateType === 'specific' ? data.dateRangeType : data.dueDateType,
    contentFixedDateType:
      data.dateRangeType === CbDueDateType.FIXED
        ? data.singleDateType ?? undefined
        : undefined,
    contentFixedDate:
      data.dateRangeType === CbDueDateType.FIXED && data.dueDate
        ? dayjs(data.dueDate).endOf('day').toDate()
        : undefined,
    contentRangeFrom:
      data.dateRangeType === CbDueDateType.RANGE && data.startDate
        ? dayjs(data.startDate).startOf('day').toDate()
        : undefined,
    contentRangeTo:
      data.dateRangeType === CbDueDateType.RANGE && data.endDate
        ? dayjs(data.endDate).endOf('day').toDate()
        : undefined,
    contentCbDisplayPeriod:
      data.dueDateType === CbDueDateType.APPLICATION_BASED
        ? data.approvalUnit
        : undefined,
    contentValueInterval:
      data.dueDateType === CbDueDateType.APPLICATION_BASED
        ? data.approvalNumber
        : undefined,
  };
};

// Shared task-level content due-date fields derived from the multi-piece form.
type UgcDueDateInputFields = Pick<
  CreateCampaignTaskV2Input,
  | 'contentDueDateType'
  | 'contentFixedDate'
  | 'contentValueInterval'
  | 'contentCbDisplayPeriod'
>;

const mapUgcFormDataToDueDateInput = (
  data: UgcDeliverableFormData
): UgcDueDateInputFields => {
  const resolvedType =
    data.dueDateType === 'specific' ? data.dateRangeType : data.dueDateType;

  return {
    contentDueDateType: resolvedType,
    contentFixedDate:
      data.dateRangeType === CbDueDateType.FIXED && data.dueDate
        ? dayjs(data.dueDate).endOf('day').toDate()
        : undefined,
    contentCbDisplayPeriod:
      data.dueDateType === CbDueDateType.APPLICATION_BASED
        ? data.approvalUnit
        : undefined,
    contentValueInterval:
      data.dueDateType === CbDueDateType.APPLICATION_BASED
        ? data.approvalNumber
        : undefined,
  };
};

// Maps the multi-piece UGC deliverable form into a UGC CreateCampaignTaskV2Input
// (a named task carrying one CONTENT content item per piece plus the shared
// content due-date config), mirroring millions-fe's mapUgcFormDataToDeliverable.
export const mapUgcDeliverableFormDataToTaskInput = (
  data: UgcDeliverableFormData
): CreateCampaignTaskV2Input => {
  const contentItems: CreateCampaignContentItemInput[] = data.contentPieces.map(
    (piece) => ({
      type: CampaignTaskType.CONTENT,
      mediaType: piece.mediaType,
      title: piece.title,
      description: piece.description,
    })
  );

  return {
    type: CampaignTaskType.UGC,
    title: data.name,
    description: data.description,
    contentItems,
    ...mapUgcFormDataToDueDateInput(data),
  };
};

/**
 * Normalizes a UGC content deliverable into a single DueDateFields object.
 * Handles the three shapes a deliverable can take in the custom-campaign flow:
 * - Flat local state produced by the ContentOnlyModal (contentDueDateType, ...)
 * - GraphQL CampaignContentTask with a nested `dueDate`
 * - GraphQL CampaignContentTask whose due date lives on its `contentItems`
 *   (merged with the parent, matching mapUgcEditingDeliverableToFormData)
 */
const resolveUgcDueDate = (deliverable: any): DueDateFields | undefined => {
  if (!deliverable) return undefined;

  // Flat local shape from the modal — the interval is already expressed in the
  // chosen display unit (days/weeks), so convert weeks back to raw days.
  if ('contentDueDateType' in deliverable && deliverable.contentDueDateType) {
    const intervalDays =
      deliverable.contentCbDisplayPeriod === CbDisplayPeriod.WEEKS
        ? (deliverable.contentValueInterval ?? 0) * 7
        : deliverable.contentValueInterval ?? null;

    return {
      __typename: 'DueDateConfiguration',
      type: deliverable.contentDueDateType,
      fixedType: deliverable.contentFixedDateType ?? null,
      fixedDate: deliverable.contentFixedDate ?? null,
      rangeFrom: deliverable.contentRangeFrom ?? null,
      rangeTo: deliverable.contentRangeTo ?? null,
      intervalDays,
    };
  }

  // GraphQL shape — the due date may sit on the parent task and/or on its
  // content item. Merge both, preferring whichever side has a value.
  const contentItem = deliverable.contentItems?.find(
    (task: any) => task.type === CampaignTaskType.CONTENT
  );

  if (deliverable.dueDate || contentItem?.dueDate) {
    return mergeDueDate(deliverable.dueDate, contentItem?.dueDate);
  }

  return undefined;
};

/**
 * Resolves the UGC media type. A loaded CampaignUgcTask carries the media type
 * on its CONTENT content item rather than the parent task, while locally built
 * deliverables keep it at the top level — check both so the media type shows in
 * either shape.
 */
export const getUgcMediaType = (
  deliverable: unknown
): CampaignMediaType | null => {
  if (!deliverable) return null;

  const item = deliverable as any;

  if (item.mediaType) return item.mediaType;

  const contentItem = item.contentItems?.find(
    (task: any) => task.type === CampaignTaskType.CONTENT
  );

  return contentItem?.mediaType ?? null;
};

const formatUgcMediaType = (mediaType: CampaignMediaType): string => {
  const lower = mediaType.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export const getUgcContentPiecesLabel = (deliverable: unknown): string => {
  if (!deliverable) return '0 content pieces';

  const item = deliverable as any;
  const pieces = item.contentItems?.filter(
    (piece: any) => piece.type !== CampaignTaskType.FINAL_CONTENT
  );
  const normalizedPieces = pieces?.length ? pieces : [item];
  const count = normalizedPieces.length;
  const mediaTypes = Array.from(
    new Set(
      normalizedPieces
        .map((piece: any) => piece.mediaType)
        .filter(Boolean) as CampaignMediaType[]
    )
  );
  const mediaTypesLabel = mediaTypes.map(formatUgcMediaType).join(', ');
  const countLabel = `${count} content piece${count === 1 ? '' : 's'}`;

  return mediaTypesLabel ? `${countLabel} (${mediaTypesLabel})` : countLabel;
};

export const getUgcContentDueDateLabel = (deliverable: unknown): string => {
  const dueDate = resolveUgcDueDate(deliverable);

  if (!dueDate?.type) return 'Content due date not set';

  switch (dueDate.type) {
    case CbDueDateType.APPLICATION_BASED: {
      if (dueDate.intervalDays) {
        const isWeeks =
          dueDate.intervalDays >= 7 && dueDate.intervalDays % 7 === 0;
        const value = isWeeks ? dueDate.intervalDays / 7 : dueDate.intervalDays;
        const unit = isWeeks ? 'week' : 'day';
        return `${value} ${unit}${
          value > 1 ? 's' : ''
        } after application approval`;
      }
      return 'After application approval';
    }

    case CbDueDateType.FIXED: {
      if (dueDate.fixedDate) {
        const dateLabel = dayjs(dueDate.fixedDate).format('MMMM D, YYYY');
        if (dueDate.fixedType === CbFixedDateType.BEFORE) {
          return `Before ${dateLabel}`;
        }
        if (dueDate.fixedType === CbFixedDateType.EXACT) {
          return `On ${dateLabel}`;
        }
        return dateLabel;
      }
      return 'Specific date not set';
    }

    case CbDueDateType.RANGE: {
      if (dueDate.rangeFrom && dueDate.rangeTo) {
        return `${dayjs(dueDate.rangeFrom).format('MMMM D')} - ${dayjs(
          dueDate.rangeTo
        ).format('MMMM D, YYYY')}`;
      }
      return 'Date range not set';
    }

    default:
      return 'Content due date not set';
  }
};

/**
 * Helper function to filter object properties based on valid fields
 */
const mapToType = <T>(obj: any, validFields: readonly string[]): T => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([key, value]) =>
        value !== undefined && value !== null && validFields.includes(key)
    )
  ) as unknown as T;
};

/**
 * Maps due date object to form data format
 * Converts days > 6 to weeks for display
 */
const mapDueDateToFormData = (dueDate?: DueDateFields | null) => {
  let intervalDays = 1;
  let displayPeriod = CbDisplayPeriod.DAYS;

  if (dueDate?.intervalDays) {
    const isWeeks = dueDate?.intervalDays > 6;
    intervalDays = isWeeks ? dueDate?.intervalDays / 7 : dueDate?.intervalDays;
    displayPeriod = isWeeks ? CbDisplayPeriod.WEEKS : CbDisplayPeriod.DAYS;
  }

  return {
    intervalDays,
    displayPeriod,
  };
};

/**
 * Transforms timeline deliverables to UGC content task input format.
 * Handles both:
 * - Nested dueDate structure (from GraphQL API)
 * - Flat structure (from local state / ContentOnlyModal)
 */
export const transformTasksToUgcContentInput = (
  items?: TimeLineDeliverable[]
): CreateCampaignContentTaskInput[] => {
  return (
    items?.map((item) => {
      const taskItem = item as any;
      const hasNestedDueDate =
        taskItem.dueDate && typeof taskItem.dueDate === 'object';

      // For items with flat structure (created locally), just filter valid fields
      if (!hasNestedDueDate) {
        return mapToType<CreateCampaignContentTaskInput>(
          taskItem,
          VALID_CAMPAIGN_CONTENT_TASK_FIELDS
        );
      }

      // For items with nested dueDate (from GraphQL), transform to flat structure
      const { dueDate, ...rest } = taskItem;
      const dueDateType = dueDate?.type;

      const isApplicationBased =
        dueDateType === CbDueDateType.APPLICATION_BASED;
      const { intervalDays: contentIntervalDays, displayPeriod } =
        mapDueDateToFormData(dueDate);

      return {
        ...mapToType<CreateCampaignContentTaskInput>(
          rest,
          VALID_CAMPAIGN_CONTENT_TASK_FIELDS
        ),
        contentDueDateType: dueDateType,
        contentFixedDate: dueDate?.fixedDate ?? undefined,
        contentFixedDateType: dueDate?.fixedType ?? undefined,
        contentRangeFrom: dueDate?.rangeFrom ?? undefined,
        contentRangeTo: dueDate?.rangeTo ?? undefined,
        ...(isApplicationBased && {
          contentValueInterval: contentIntervalDays,
          contentCbDisplayPeriod: displayPeriod,
        }),
      };
    }) ?? []
  );
};
